import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";

const CategoryManager = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({});
  const [totalItems, setTotalItems] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (localStorage.getItem("userData")) {
      setUserRole(JSON.parse(localStorage.getItem("userData")));
    }
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [searchQuery, currentPage]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/category/all`, {
        params: {
          search: searchQuery,
          page: currentPage,
          limit: rowsPerPage
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `${localStorage.getItem("token")}`,
        }
      });
      setCategories(response.data.data || []);
      setPagination(response.data.pagination || {});
      setTotalItems(response.data.pagination?.totalItems || 0);
    } catch (error) {
      toast.error("Failed to fetch categories");
      console.error("Error fetching categories:", error);
    }
  };


  const validateCategoryName = (name) => {
    if (name.length < 3) {
      return "Category name must be at least 3 characters long";
    }
    if (name.length > 50) {
      return "Category name must not exceed 50 characters";
    }
    return "";
  };

  const handleAddCategory = async () => {
    const error = validateCategoryName(categoryName);
    if (error) {
      setCategoryError(error);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/category/create`,
        { name: categoryName },
        { headers: {
          "Content-Type": "application/json",
          Authorization: `${localStorage.getItem("token")}`,
        } }
      );
      toast.success("Category added successfully");
      setIsAddModalOpen(false);
      setCategoryName("");
      setCategoryError("");
      fetchCategories();
      setCurrentPage(1);
    } catch (error) {
      toast.error("Failed to add category");
      console.error("Error adding category:", error);
    }
  };

  const handleUpdateCategory = async () => {
    const error = validateCategoryName(categoryName);
    if (error) {
      setCategoryError(error);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/category/update/${selectedCategory._id}`,
        { name: categoryName },
        { headers: {
          "Content-Type": "application/json",
          Authorization: `${localStorage.getItem("token")}`,
        }}
      );
      toast.success("Category updated successfully");
      setIsUpdateModalOpen(false);
      setCategoryName("");
      setCategoryError("");
      setSelectedCategory(null);
      fetchCategories();
      setCurrentPage(1);
    } catch (error) {
      toast.error("Failed to update category");
      console.error("Error updating category:", error);
    }
  };

  const handleDeleteCategory = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${process.env.REACT_APP_BASE_URL}/category/delete/${selectedCategory._id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${localStorage.getItem("token")}`,
        }
      });
      toast.success("Category deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedCategory(null);
      fetchCategories();
      setCurrentPage(1);
    } catch (error) {
      toast.error("Failed to delete category");
      console.error("Error deleting category:", error);
    }
  };

  const openUpdateModal = (category) => {
    setSelectedCategory(category);
    setCategoryName(category.name);
    setCategoryError("");
    setIsUpdateModalOpen(true);
  };

  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setIsDeleteModalOpen(true);
  };


  return (
    <div className="flex min-h-screen bg-gray">
      <Sidebar />
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray">Category Manager</h1>
            </div>

            <div className="mb-6">
              <div className="flex gap-4 items-center">
                <input
                  type="text"
                  placeholder="Search by category name..."
                  value={searchQuery}
                  onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                  className="flex-1 px-4 py-2 border border-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-blue"
                />
                 <button
                onClick={() => {
                  setCategoryName("");
                  setCategoryError("");
                  setIsAddModalOpen(true);
                }}
                className="flex items-center gap-2 bg-blue text-white px-4 py-2 rounded-lg hover:bg-blue transition-colors"
              >
                <FaPlus />
                Add Category
              </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray border-b">
                    <th className="text-left p-3 font-semibold text-gray">S.No</th>
                    <th className="text-left p-3 font-semibold text-gray">Category Name</th>
                    <th className="text-left p-3 font-semibold text-gray">Created At</th>
                    <th className="text-left p-3 font-semibold text-gray">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.length > 0 ? (
                    categories.map((category, index) => (
                      <tr key={category._id} className="border-b hover:bg-gray">
                        <td className="p-3 text-gray">
                          {(currentPage - 1) * rowsPerPage + index + 1}
                        </td>
                        <td className="p-3 text-gray font-medium">{category.name}</td>
                        <td className="p-3 text-gray">
                          {new Date(category.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openUpdateModal(category)}
                              className="text-blue hover:text-blue transition-colors"
                              title="Update"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => openDeleteModal(category)}
                              className="text-red hover:text-red transition-colors"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center p-8 text-gray">
                        No categories found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex justify-center mt-6 gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={!pagination.hasPrevPage}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray"
                >
                  Previous
                </button>
                <span className="px-3 py-1">
                  Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} items)
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={!pagination.hasNextPage}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Category Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">Add Category</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category Name
              </label>
              <input
                type="text"
                value={categoryName}
                onChange={(e) => {
                  setCategoryName(e.target.value);
                  setCategoryError("");
                }}
                className="w-full px-3 py-2 border border-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-blue"
                placeholder="Enter category name"
              />
              {categoryError && (
                <p className="text-red text-sm mt-1">{categoryError}</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsAddModalOpen(false);
                  setCategoryName("");
                  setCategoryError("");
                }}
                className="px-4 py-2 border border-gray rounded-lg hover:bg-gray"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                className="px-4 py-2 bg-blue text-white rounded-lg hover:bg-blue"
              >
                Add Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Category Modal */}
      {isUpdateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">Update Category</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category Name
              </label>
              <input
                type="text"
                value={categoryName}
                onChange={(e) => {
                  setCategoryName(e.target.value);
                  setCategoryError("");
                }}
                className="w-full px-3 py-2 border border-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-blue"
                placeholder="Enter category name"
              />
              {categoryError && (
                <p className="text-red text-sm mt-1">{categoryError}</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsUpdateModalOpen(false);
                  setCategoryName("");
                  setCategoryError("");
                  setSelectedCategory(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCategory}
                className="px-4 py-2 bg-blue text-white rounded-lg hover:bg-blue"
              >
                Update Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the category "
              <span className="font-semibold">{selectedCategory?.name}</span>"?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setSelectedCategory(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                No
              </button>
              <button
                onClick={handleDeleteCategory}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManager;
